<template>
  <div class="space-y-6">
    <div class="flex items-center gap-4">
      <input v-model="view.q" :class="[SIZED_INPUT, 'w-72']" placeholder="Filter by name or unit" aria-label="Filter templates">
      <select v-model="view.type" :class="[SIZED_INPUT, 'w-56']" aria-label="Run type">
        <option value="">Any run type</option>
        <option value="batch">Batch</option>
        <option value="single">Single run</option>
      </select>
      <label class="inline-flex items-center gap-2 whitespace-nowrap text-gray-700"><input v-model="view.inactive" type="checkbox" class="rounded border-gray-300"> Show inactive</label>
      <span class="whitespace-nowrap text-gray-500">{{ shown.length === templates.length ? `${templates.length} templates` : `${shown.length} of ${templates.length}` }}</span>
      <router-link :to="{ name: 'AdminHermesTemplate', params: { templateId: 'new' } }" :class="[PRIMARY_BUTTON, 'ml-auto']">New template</router-link>
    </div>

    <div v-if="toast" role="alert" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ toast }}</div>
    <div v-if="error" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">
      <strong>Templates could not be loaded.</strong> {{ error }} <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="load">Try again</button>
    </div>

    <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table class="w-full table-fixed divide-y divide-gray-200">
        <colgroup><col style="width:4%"><col style="width:17%"><col style="width:9%"><col style="width:14%"><col style="width:10%"><col style="width:7%"><col style="width:14%"><col style="width:13%"><col style="width:6%"><col style="width:6%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH"><span class="sr-only">Edit</span></th><th :class="TH">Template</th><th :class="TH">Unit</th><th :class="TH">Population</th><th :class="TH">Inputs</th>
          <th :class="[TH, 'text-right']">Result fields</th><th :class="TH">Effects</th><th :class="TH">Last run</th><th :class="TH">Active</th><th :class="[TH, 'text-right']">Actions</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <template v-if="loading"><tr v-for="n in 3" :key="n"><td v-for="c in 10" :key="c" :class="TD"><div class="h-4 rounded bg-gray-100" /></td></tr></template>
          <tr v-else-if="!templates.length"><td colspan="10" class="px-3 py-8 text-center text-gray-500">No templates yet.</td></tr>
          <tr v-else-if="!shown.length"><td colspan="10" class="px-3 py-8 text-center text-gray-500">No templates match these filters.</td></tr>
          <tr v-for="t in shown" v-else :key="t.id" :class="t.is_active ? '' : 'text-gray-500'">
            <td :class="[TD, 'py-2']">
              <router-link v-tooltip="'Edit template'" :to="{ name: 'AdminHermesTemplate', params: { templateId: t.id } }" class="inline-flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-900" :aria-label="`Edit ${t.name}`">
                <font-awesome-icon :icon="['fas', 'pen']" class="text-xs" />
              </router-link>
            </td>
            <td :class="TD">
              <router-link
                v-tooltip="{ text: t.description, placement: 'bottom-start' }"
                :to="{ name: 'AdminHermesTemplate', params: { templateId: t.id } }"
                class="font-medium text-gray-900 hover:underline"
              >
                {{ t.name }}
              </router-link>
              <div class="text-gray-500">#{{ t.id }} · v{{ t.version }}</div>
              <div v-if="t.params.length" class="text-gray-500">Run params: {{ t.params.map((p) => p.name + (p.required ? '' : '?')).join(', ') }}</div>
            </td>
            <td :class="TD"><span class="block truncate" :title="t.unit">{{ t.unit || '—' }}</span></td>
            <td :class="TD">
              <a v-if="t.population" :href="$router.resolve(dataAttributeLocation(t.population.ref)).href" target="_blank" rel="noopener" class="block break-words text-neutral-800 hover:underline">{{ daName(t.population.ref) }} #{{ t.population.ref }}</a>
              <span v-else>Single run</span>
            </td>
            <td :class="TD"><div v-for="(n, k) in kindCounts(t)" :key="k">{{ n }} {{ words(k) }}</div></td>
            <td :class="[TD, 'text-right tabular-nums']">{{ fieldCount(t) || '—' }}</td>
            <td :class="TD">
              <span v-for="k in t.effects" :key="k" class="inline-block max-w-full truncate align-top rounded bg-gray-100 px-2 mr-1 mb-1" :title="k">{{ k }}</span>
              <span v-if="!t.effects.length" class="text-gray-500">None</span>
            </td>
            <td :class="TD">
              <template v-if="t.last_run && t.last_run.last_run_id">
                <HermesBadge :value="t.last_run.last_run_status" />
                <div class="text-gray-500 mt-1">
                  <router-link :to="{ name: 'AdminHermesRun', params: { runId: t.last_run.last_run_id } }" class="text-neutral-800">#{{ t.last_run.last_run_id }}</router-link>
                  · <span :title="absolute(t.last_run.last_run_at)">{{ relative(t.last_run.last_run_at) }}</span>
                </div>
              </template>
              <span v-else class="text-gray-500">Never run</span>
            </td>
            <td :class="TD">
              <label class="inline-flex items-center gap-2"><input type="checkbox" :checked="t.is_active" :aria-label="`${t.name} active`" @click.prevent="request(t)"> {{ t.is_active ? 'On' : 'Off' }}</label>
            </td>
            <td :class="[TD, 'py-2 text-right']">
              <router-link v-if="t.is_active" v-tooltip="'Run this template'" :to="{ name: 'AdminHermesRuns', query: { start: t.id } }" class="inline-flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-900" :aria-label="`Run ${t.name}`">
                <font-awesome-icon :icon="['fas', 'play']" class="text-xs" />
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="turningOff" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" aria-labelledby="hermes-turn-off-title">
      <div class="w-[460px] rounded-lg bg-white border border-gray-200">
        <div id="hermes-turn-off-title" class="bg-primary-100 px-4 py-3 rounded-t-lg font-semibold text-gray-900">Turn off {{ turningOff.name }}?</div>
        <div class="p-4 text-gray-700">
          <p>No new runs will start, including scheduled and event-triggered ones. Runs in progress will finish. You can turn it back on anytime.</p>
        </div>
        <div class="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
          <button type="button" :class="SECONDARY_BUTTON" @click="turningOff = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" @click="confirmOff">Turn off</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import tooltip from '@/directives/tooltip';
import queryState from '@/mixins/queryState';
import HermesBadge from './HermesBadge.vue';
import { API, INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, errorOf, dataAttributeLocation, relative, schemaFields, words } from './hermes';

export default {
  name: 'HermesTemplates',
  components: { HermesBadge },
  directives: { tooltip },
  mixins: [queryState('view', { q: '', type: '', inactive: false })],
  data() {
    return {
      templates: [], das: {}, loading: true, error: '', toast: '', turningOff: null,
      INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    shown() {
      const q = this.view.q.trim().toLowerCase();
      return this.templates
        .filter((t) => this.view.inactive || t.is_active)
        .filter((t) => !this.view.type || (this.view.type === 'batch' ? t.population : !t.population))
        .filter((t) => !q || [t.name, t.unit].some((v) => String(v ?? '').toLowerCase().includes(q)))
        .sort((a, b) => (b.is_active - a.is_active) || String(b.updated_at).localeCompare(String(a.updated_at)));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute, dataAttributeLocation, relative, words,
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const [t, c] = await Promise.all([this.$axios.get(`${API}/templates/`), this.$axios.get(`${API}/templates/catalog/`)]);
        this.templates = t.data;
        this.das = Object.fromEntries(c.data.data_attributes.map((d) => [d.id, d.name]));
      } catch (e) {
        this.error = errorOf(e, 'Try again in a moment.');
      } finally {
        this.loading = false;
      }
    },
    daName(id) {
      return this.das[id] || 'DataAttribute';
    },
    kindCounts(t) {
      const out = {};
      (t.inputs || []).forEach((i) => { out[i.kind] = (out[i.kind] || 0) + 1; });
      return out;
    },
    fieldCount(t) {
      return schemaFields(t.result_schema).length;
    },
    request(t) {
      if (t.is_active) this.turningOff = t;
      else this.toggle(t);
    },
    confirmOff() {
      const t = this.turningOff;
      this.turningOff = null;
      this.toggle(t);
    },
    async toggle(t) {
      this.toast = '';
      const was = t.is_active;
      t.is_active = !was;
      try {
        await this.$axios.patch(`${API}/templates/${t.id}/`, { is_active: t.is_active });
      } catch (e) {
        t.is_active = was;
        this.toast = errorOf(e, 'The change was not saved.');
      }
    },
  },
};
</script>
